/**
 * <TutorSessionFeedCard> — une carte du bac « À décider » de la session avec Sam (molecule).
 *
 * **Compacte, le détail à la demande** (Wajih, 04/11, inspiré des chats de livestream) : une ligne — la photo de
 * l'élève et le signe du signal posé dessus, la phrase au prénom en gras, le temps écoulé, la croix —, l'action
 * dessous, alignée sur la phrase. Un
 * appui sur la phrase déplie ce qui ne sert qu'à décider : sa condition de validité, l'extrait entendu, la phrase
 * entière. L'escalade ne se replie jamais.
 *
 * **Une carte = une décision** : c'est la seule forme du fil qui a un liseré et une action pleine. Elle se lit au
 * même poids pour l'élève regardé et dans « Ailleurs » (la même carte, son prénom en tête).
 *
 * **La priorité se voit** : une entrée `urgent` (la main, le silence) est teintée d'alerte, avec son liseré ;
 * l'escalade (quatre minutes sans réponse) la passe au rouge et le dit. Les autres sont des cartes blanches ;
 * celles où le champion s'en occupe (`low`) restent à plat.
 *
 * **La signature du champion** : il agit (sa tête au bout de la ligne), ou il propose (`sam="proposes"` : la carte
 * en pointillés, une proposition n'est pas encore un fait).
 *
 * La croix écarte une entrée qui le permet (`dismissible`) ; sur une main levée, elle **baisse la main**
 * (l'OK de l'ancienne pastille, le seul geste d'un cours individuel, #2876) et le dit : sur un appareil qui survole (une souris),
 * elle ne paraît qu'au survol ou au clavier ; au doigt — une tablette dans un navigateur comprise —,
 * elle est toujours là.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UITutorFeedItem,
    type UITutorFeedPriority,
    type UITutorFeedSignal,
} from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { inkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { Card } from "../Card"
import { type AvatarSign, SignedAvatar } from "../SignedAvatar"
import { canHover } from "../../utils/hover"

export type TutorSessionFeedCardProps = {
    item: UITutorFeedItem
    onAction: (actionId: string) => void
    /** La croix, sur une carte `dismissible`. Absente : pas de croix. */
    onDismiss?: () => void
    /** L'action qui part : son bouton tourne. */
    pendingActionId?: string
    /** Regarder l'élève (« Voir <prénom> », à côté de l'action), dans « À traiter » qui les montre tous. Absent : rien. */
    onLook?: () => void
    /** Le champion qui aide en séance (le monde de jeu de l'espace) : sa tête et son prénom signent la carte. Absent : pas de signature. */
    helper?: ChampionName
    testID?: string
}

const TEXTS = {
    escalated: "Sans réponse — à toi de jouer",
    validWhile: (condition: string) => `Valable ${condition}`,
    proposes: (name: string) => `${name} propose`,
    dismiss: "Écarter",
    lowerHand: (firstname: string) => `Baisser la main de ${firstname}`,
    more: (firstname: string, text: string) => `${firstname} ${text}, le détail`,
    look: (firstname: string) => `Voir ${firstname}`,
}

/** Le signe de chaque signal, posé sur la photo (les emoji de l'ancienne carte). */
const SIGNAL_ICON: Record<UITutorFeedSignal, IconName> = {
    hand_raised: "hand-paper",
    silence: "stopwatch",
    out_of_app: "ghost",
    exercise_done: "check",
    auto_activity: "compass",
    sam_ongoing: "comment-dots",
    sam_summary: "comment-dots",
    sam_proposal: "lightbulb",
    sam_whisper: "lightbulb",
    sam_nudge: "volume-up",
}

type PriorityFace = {
    background?: SemanticColorKey
    rail?: SemanticColorKey
    sign: SemanticColorKey
    ink: SemanticColorKey
}

/**
 * Ce que la priorité change : le fond, le liseré, la pastille du signe. L'encre d'une pastille pleine est
 * celle de son aplat (`inkOnFill` : sur l'orange plein, l'encre foncée ; le blanc n'y tient pas 3:1).
 */
const PRIORITY_FACE: Record<UITutorFeedPriority | "escalated", PriorityFace> = {
    escalated: { background: "dangerLight", rail: "danger", sign: "danger", ink: inkOnFill("danger") },
    urgent: { background: "warningLight", rail: "warning", sign: "warning", ink: inkOnFill("warning") },
    normal: { rail: "role", sign: "surface-subtle", ink: "text-strong" },
    low: { sign: "surface-subtle", ink: "text-muted" },
}

/** Le signe d'une entrée, et sa pastille : la carte le pose sur la photo, le fil replié aussi. */
export function tutorFeedSign(item: UITutorFeedItem): AvatarSign {
    const face = PRIORITY_FACE[item.escalated === true ? "escalated" : item.priority]
    return { icon: SIGNAL_ICON[item.signal], color: face.sign, ink: face.ink }
}

/** La photo ou l'initiale de l'élève : petite, la phrase est l'information. */
const FEED_AVATAR_SIZE = 24
const SAM_SIZE = 16
/**
 * Un pointeur qui survole (une souris) : la croix attend le survol. Au doigt — le natif, ou une tablette
 * dans un navigateur, qui n'a pas de survol —, elle reste. Lu une fois : l'appareil ne change pas.
 */
const HOVER_ONLY = canHover()

export function TutorSessionFeedCard(props: TutorSessionFeedCardProps) {
    const { item, onAction, onDismiss, onLook, pendingActionId, helper, testID } = props
    const proposes = item.sam === "proposes"
    // État d'interface : la carte survolée, ou sa croix au clavier, montre la croix (le web) ; le détail déplié.
    const [hovered, setHovered] = useState(false)
    const [crossFocused, setCrossFocused] = useState(false)
    const [open, setOpen] = useState(false)
    const dismissible = item.dismissible === true && onDismiss != null
    // Sur une main levée, la croix devient la coche qui la baisse : toujours là, c'est un geste.
    const lowersHand = item.signal === "hand_raised"
    const crossVisible = lowersHand || !HOVER_ONLY || hovered || crossFocused
    const face = PRIORITY_FACE[item.escalated === true ? "escalated" : item.priority]
    const action = item.action
    const detail = item.validWhile != null || item.quote != null

    const card = (
        <Card
            p="2"
            leftBorderColor={proposes ? undefined : face.rail}
            background={face.background}
            elevation={proposes || item.priority === "low" ? "none" : "card"}
            style={proposes ? styles.proposal : undefined}
            testID={testID}
        >
            <Stack gap="1">
                <Row gap="2" align="center">
                    <SignedAvatar
                        uri={item.student.avatarUrl}
                        name={item.student.firstname}
                        size={FEED_AVATAR_SIZE}
                        sign={tutorFeedSign(item)}
                    />
                    <Pressable
                        onPress={() => setOpen(!open)}
                        accessibilityRole="button"
                        accessibilityLabel={TEXTS.more(item.student.firstname, item.text)}
                        aria-expanded={open}
                        style={styles.sentence}
                    >
                        <Text size="text-sm" color="text-primary" numberOfLines={open ? 0 : 1}>
                            <Text size="text-sm" weight="bold" color="text-strong">
                                {item.student.firstname}
                            </Text>{" "}
                            {item.text}
                        </Text>
                    </Pressable>
                    <Text size="text-xs" color="text-muted">
                        {item.elapsed}
                    </Text>
                    {item.sam != null && helper != null && (
                        <View
                            accessibilityLabel={
                                proposes ? TEXTS.proposes(CHAMPIONS[helper].label) : CHAMPIONS[helper].label
                            }
                        >
                            <ChampionAvatar name={helper} size={SAM_SIZE} />
                        </View>
                    )}
                    {dismissible && (
                        <View
                            style={styles.cross(crossVisible)}
                            onFocus={() => setCrossFocused(true)}
                            onBlur={() => setCrossFocused(false)}
                        >
                            <IconButton
                                iconName={lowersHand ? "check" : "times"}
                                appearance="transparent"
                                iconColor="text-muted"
                                size={24}
                                iconSize={14}
                                onPress={onDismiss}
                                accessibilityLabel={
                                    lowersHand
                                        ? TEXTS.lowerHand(item.student.firstname)
                                        : `${TEXTS.dismiss} : ${item.student.firstname} ${item.text}`
                                }
                            />
                        </View>
                    )}
                </Row>

                {item.escalated === true && (
                    <Text size="text-xs" weight="bold" color="danger-strong" style={styles.under}>
                        {TEXTS.escalated}
                    </Text>
                )}

                {(action != null || onLook != null) && (
                    // L'action, sous la phrase : la phrase garde sa ligne entière, l'action se lit d'un coup d'œil.
                    <Row gap="2" align="center" style={styles.actions}>
                        {action != null && action.passive !== true && (
                            <Button
                                label={action.label}
                                size="sm"
                                color={item.escalated === true ? "danger" : undefined}
                                loading={action.id === pendingActionId}
                                onPress={() => onAction(action.id)}
                            />
                        )}
                        {action != null && action.passive === true && (
                            <TextButton
                                label={action.label}
                                size="sm"
                                loading={action.id === pendingActionId}
                                onPress={() => onAction(action.id)}
                            />
                        )}
                        {onLook != null && (
                            <TextButton label={TEXTS.look(item.student.firstname)} size="sm" onPress={onLook} />
                        )}
                    </Row>
                )}

                {open && detail && (
                    <Stack gap="1" style={styles.under}>
                        {item.validWhile != null && (
                            <Text size="text-xs" color="text-muted">
                                {TEXTS.validWhile(item.validWhile)}
                            </Text>
                        )}
                        {item.quote != null && (
                            <Text size="text-sm" color="text-primary" style={styles.quote}>
                                « {item.quote} »
                            </Text>
                        )}
                    </Stack>
                )}
            </Stack>
        </Card>
    )
    // Seule une carte qui a une croix à cacher suit le survol.
    if (!HOVER_ONLY || !dismissible) return card
    return (
        <Pressable
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
            accessible={false}
            focusable={false}
        >
            {card}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    sentence: { flex: 1, minWidth: 0 },
    quote: { fontStyle: "italic" as const },
    /** L'action et le détail s'alignent sous la phrase, pas sous la photo. */
    under: { paddingLeft: FEED_AVATAR_SIZE + theme.spacing["2"] },
    // Étroite, « Voir <prénom> » passe à la ligne au lieu de sortir de la carte (relevé en recette du 05/11, à 1024 px).
    actions: { paddingLeft: FEED_AVATAR_SIZE + theme.spacing["2"], flexWrap: "wrap" as const },
    cross: (visible: boolean) => ({ opacity: visible ? 1 : 0 }),
    /** Une proposition : la carte en pointillés, sans ombre — pas encore un fait. */
    proposal: {
        borderWidth: 1.5,
        borderStyle: "dashed" as const,
        borderColor: theme.colors["border"],
    },
}))
